import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { Modal, Button, Form, InputGroup, Badge } from "react-bootstrap";
import {
  FiSearch,
  FiPlus,
  FiMinus,
  FiTrash2,
  FiSend,
  FiUser,
  FiClock,
  FiGift,
  FiShoppingCart,
  FiCreditCard,
  FiDollarSign,
  FiPackage,
  FiChevronDown,
} from "react-icons/fi";
import api from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import ConfirmModal from "../components/ConfirmModal";
import bluetoothPrinter from "../utils/bluetooth";

const POS = () => {
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [weightKg, setWeightKg] = useState("");
  const [totalPcs, setTotalPcs] = useState("");
  const [clothingNotes, setClothingNotes] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [discountType, setDiscountType] = useState("flat");
  const [discountAmount, setDiscountAmount] = useState("");
  const [voucherCode, setVoucherCode] = useState("");
  const [validatedVoucher, setValidatedVoucher] = useState(null);
  const [notes, setNotes] = useState("");
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [newCustNameSuggestion, setNewCustNameSuggestion] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCustomerPicker, setShowCustomerPicker] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: "", phone: "" });
  const [activeTab, setActiveTab] = useState("reko");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [productFilter, setProductFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [loyaltyRules, setLoyaltyRules] = useState([]);
  const [loyaltyEnabled, setLoyaltyEnabled] = useState(false);
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [redeemRuleId, setRedeemRuleId] = useState("");
  const [serviceCategories, setServiceCategories] = useState([]);
  const [shiftReady, setShiftReady] = useState(false);
  const [showShiftModal, setShowShiftModal] = useState(false);
  const [shiftOpeningCash, setShiftOpeningCash] = useState("");
  const [staleShift, setStaleShift] = useState(null);
  const [specialPaymentMethods, setSpecialPaymentMethods] = useState([]);
  const [selectedSpecialPayment, setSelectedSpecialPayment] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: "",
    message: null,
    onConfirm: null,
    confirmLabel: "Lanjutkan",
    confirmVariant: "warning",
  });
  const closeConfirm = () =>
    setConfirmModal((prev) => ({ ...prev, show: false, onConfirm: null }));

  // WA invoice confirmation after checkout
  const [showWAInvoiceConfirm, setShowWAInvoiceConfirm] = useState(false);
  const [waInvoiceTxId, setWaInvoiceTxId] = useState(null);
  const [waInvoicePhone, setWaInvoicePhone] = useState(null);
  const [waInvoiceCustomerName, setWaInvoiceCustomerName] = useState(null);
  const [waInvoiceSending, setWaInvoiceSending] = useState(false);

  const [posSettings, setPosSettings] = useState({});

  // DP / Partial payment state
  const [dpAmount, setDpAmount] = useState("");
  const [dpMethod, setDpMethod] = useState("cash");

  // Split payment state
  const [splitRows, setSplitRows] = useState([
    { method: "cash", amount: "" },
    { method: "qris", amount: "" },
  ]);

  // === REKOMENDASI STATE ===

  const [rekoLayanan, setRekoLayanan] = useState("cuci_setrika"); // cuci_kering | cuci_setrika | setrika_saja
  const [rekoSpeed, setRekoSpeed] = useState("reguler"); // reguler | express | super_express
  const [rekoResult, setRekoResult] = useState(null);
  const [showRekoModal, setShowRekoModal] = useState(false);

  // Printer connect modal (for auto-print after checkout)
  const [showPOSPrinterModal, setShowPOSPrinterModal] = useState(false);
  const [posPrinterConnecting, setPosPrinterConnecting] = useState(false);
  const [pendingPrintData, setPendingPrintData] = useState(null);

  // Check active shift for kasir on mount
  useEffect(() => {
    const checkShift = async () => {
      if (user?.role === "admin") {
        setShiftReady(true);
        return;
      }
      try {
        const res = await api.get("/shifts/current");
        if (res.data.active) {
          if (res.data.is_today) {
            setShiftReady(true);
          } else {
            // Shift dari hari sebelumnya masih open
            setStaleShift(res.data.shift);
            setShowShiftModal(true);
          }
        } else {
          setShowShiftModal(true);
        }
      } catch {
        setShowShiftModal(true);
      }
    };
    checkShift();
  }, [user]);

  const handleCloseStaleShift = async () => {
    try {
      await api.post("/shifts/close", {
        closing_cash: 0,
        notes: "Auto-close shift hari sebelumnya",
      });
      setStaleShift(null);
      // Modal tetap terbuka, sekarang tampilkan form buka shift baru
    } catch (error) {
      toast.error(error.response?.data?.message || "Gagal menutup shift lama");
    }
  };

  const handleOpenShift = async () => {
    if (shiftOpeningCash === "" || Number(shiftOpeningCash) < 0) {
      toast.error("Masukkan kas awal yang valid (boleh 0)");
      return;
    }
    try {
      await api.post("/shifts/open", {
        opening_cash: Number(shiftOpeningCash),
      });
      toast.success("Shift berhasil dibuka");
      setShowShiftModal(false);
      setShiftOpeningCash("");
      setShiftReady(true);
    } catch (error) {
      toast.error(error.response?.data?.message || "Gagal membuka shift");
    }
  };

  const loadData = useCallback(async () => {
    try {
      const [svcRes, prodRes, custRes, settRes, catRes, rulesRes, pmRes] =
        await Promise.all([
          api.get("/services/active"),
          api.get("/products/active"),
          api.get("/customers"),
          api.get("/settings"),
          api.get("/services/categories"),
          api.get("/settings/loyalty-rules"),
          api.get("/payment-methods/active"),
        ]);
      setServices(svcRes.data);
      setProducts(prodRes.data);
      setCustomers(custRes.data);
      setServiceCategories(catRes.data || []);
      setLoyaltyRules(rulesRes.data.filter((r) => r.is_active));
      setSpecialPaymentMethods(Array.isArray(pmRes.data) ? pmRes.data : []);
      if (settRes.data) {
        setLoyaltyEnabled(settRes.data.loyalty_enabled === "1");
        setPosSettings(settRes.data);
      }
    } catch (error) {
      toast.error("Gagal memuat data");
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const addToCart = (item, type) => {
    const existing = cart.find((c) => c.id === item.id && c.type === type);
    if (existing) {
      if (type === "product" && existing.quantity >= item.stock) {
        toast.warning(`Stok ${item.name} tidak mencukupi`);
        return;
      }
      setCart(
        cart.map((c) =>
          c.id === item.id && c.type === type
            ? { ...c, quantity: c.quantity + 1 }
            : c,
        ),
      );
    } else {
      setCart([
        ...cart,
        {
          id: item.id,
          type,
          name: item.name,
          price: Number(item.price || item.sell_price),
          quantity: 1,
          maxStock: type === "product" ? item.stock : 999,
          discount: 0,
          category_name: item.category_name || "",
          price_type: item.price_type || null,
        },
      ]);
    }
    toast.success(`${item.name} ditambahkan`, {
      autoClose: 1000,
      hideProgressBar: true,
    });
  };

  const updateQuantity = (index, delta) => {
    const newCart = [...cart];
    const item = newCart[index];
    const step = item.price_type === "per_kg" ? 0.5 : 1;
    const newQty = Math.round((item.quantity + delta * step) * 10) / 10;
    if (newQty <= 0) {
      newCart.splice(index, 1);
    } else if (item.type === "product" && newQty > item.maxStock) {
      toast.warning("Stok tidak mencukupi");
      return;
    } else {
      item.quantity = newQty;
    }
    setCart(newCart);
  };

  const setQuantityDirect = (index, val) => {
    const newQty = parseFloat(val);
    if (!val || val === "") return; // jangan hapus saat user sedang mengetik
    const newCart = [...cart];
    const item = newCart[index];
    if (isNaN(newQty) || newQty <= 0) {
      newCart.splice(index, 1);
    } else if (item.type === "product" && newQty > item.maxStock) {
      toast.warning("Stok tidak mencukupi");
      return;
    } else {
      item.quantity = newQty;
    }
    setCart(newCart);
  };

  const removeFromCart = (index) => setCart(cart.filter((_, i) => i !== index));

  const subtotal = cart.reduce(
    (sum, item) => sum + (item.price * item.quantity - item.discount),
    0,
  );
  const manualDiscount =
    discountType === "percentage"
      ? subtotal * (Number(discountAmount) / 100)
      : Number(discountAmount) || 0;
  const voucherDiscount = validatedVoucher
    ? validatedVoucher.discount_type === "percentage"
      ? subtotal * (validatedVoucher.discount_value / 100)
      : Number(validatedVoucher.discount_value)
    : 0;
  const totalDiscount = manualDiscount + voucherDiscount;
  const baseTotal = Math.max(0, subtotal - totalDiscount);
  const total =
    selectedSpecialPayment && selectedSpecialPayment.override_price != null
      ? Number(selectedSpecialPayment.override_price)
      : baseTotal;
  const totalItems = cart.reduce((s, i) => s + i.quantity, 0);

  const validateVoucher = async () => {
    if (!voucherCode) return;
    try {
      const res = await api.post("/promos/vouchers/validate", {
        code: voucherCode,
      });
      if (res.data.valid) {
        setValidatedVoucher(res.data.voucher);
        toast.success("Voucher valid!");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Voucher tidak valid");
      setValidatedVoucher(null);
    }
  };

  const openPayOrWarn = () => {
    if (cart.length === 0) {
      toast.error("Keranjang kosong");
      return;
    }
    if (!selectedCustomer) {
      toast.error("Pilih customer terlebih dahulu");
      return;
    }
    if (!totalPcs || parseInt(totalPcs) <= 0) {
      toast.error("Jumlah pcs pakaian wajib diisi");
      return;
    }
    setShowPaymentModal(true);
  };

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast.error("Keranjang kosong");
      return;
    }
    if (
      paymentMethod !== "unpaid" &&
      paymentMethod !== "special" &&
      paymentMethod !== "partial" &&
      paymentMethod === "cash" &&
      Number(paymentAmount) < total
    ) {
      toast.error("Pembayaran kurang");
      return;
    }
    if (paymentMethod === "partial") {
      const dp = Number(dpAmount);
      if (!dp || dp <= 0) {
        toast.error("Masukkan jumlah DP");
        return;
      }
      if (dp >= total) {
        toast.error("DP harus kurang dari total. Gunakan pembayaran penuh.");
        return;
      }
    }
    if (paymentMethod === "split") {
      const validMethods = ["cash", "debit", "credit", "qris", "transfer"];
      for (const row of splitRows) {
        if (
          !validMethods.includes(row.method) ||
          !Number(row.amount) ||
          Number(row.amount) <= 0
        ) {
          toast.error("Isi jumlah untuk setiap metode bayar");
          return;
        }
      }
      const splitTotal = splitRows.reduce(
        (s, r) => s + Number(r.amount || 0),
        0,
      );
      if (Math.abs(splitTotal - total) > 1) {
        toast.error(
          `Total split ${fmt(splitTotal)} ≠ total tagihan ${fmt(total)}`,
        );
        return;
      }
    }
    setLoading(true);
    try {
      const items = cart.map((item) => ({
        type: item.type,
        id: item.id,
        quantity: item.quantity,
        discount: item.discount,
      }));
      const splitPayloads =
        paymentMethod === "split"
          ? splitRows.map((r) => ({
              method: r.method,
              amount: Number(r.amount),
            }))
          : undefined;
      const res = await api.post("/transactions", {
        customer_id: selectedCustomer?.id,
        weight_kg: weightKg ? Number(weightKg) : null,
        total_pcs: totalPcs ? Number(totalPcs) : null,
        clothing_notes: clothingNotes || null,
        items,
        payment_method: selectedSpecialPayment ? "special" : paymentMethod,
        payment_amount:
          paymentMethod === "unpaid" || paymentMethod === "partial"
            ? 0
            : paymentMethod === "split"
              ? splitRows.reduce((s, r) => s + Number(r.amount || 0), 0)
              : paymentMethod === "cash"
                ? Number(paymentAmount)
                : total,
        discount_amount: selectedSpecialPayment ? 0 : manualDiscount,
        voucher_id: selectedSpecialPayment ? undefined : validatedVoucher?.id,
        special_payment_method_id: selectedSpecialPayment?.id || null,
        special_payment_override_price:
          selectedSpecialPayment &&
          selectedSpecialPayment.override_price != null
            ? Number(selectedSpecialPayment.override_price)
            : null,
        dp_amount: paymentMethod === "partial" ? Number(dpAmount) : undefined,
        dp_payment_method: paymentMethod === "partial" ? dpMethod : undefined,
        split_payments: splitPayloads,
        notes: notes || null,
      });
      toast.success(`Transaksi berhasil! ${res.data.invoice_number}`);

      // Show WA invoice confirmation if customer has phone
      if (selectedCustomer?.phone) {
        setWaInvoiceTxId(res.data.id);
        setWaInvoicePhone(selectedCustomer.phone);
        setWaInvoiceCustomerName(selectedCustomer.name);
        setShowWAInvoiceConfirm(true);
      }

      // Print receipt via Bluetooth
      const printData = {
        invoice_number: res.data.invoice_number,
        id: res.data.id,
        created_at: new Date(),
        cashier_name: user?.full_name || user?.username || "",
        customer_name: selectedCustomer?.name || null,
        weight_kg: weightKg ? Number(weightKg) : null,
        total_pcs: totalPcs ? Number(totalPcs) : null,
        clothing_notes: clothingNotes || null,
        details: cart.map((item) => ({
          item_type: item.type,
          item_name: item.name,
          unit_price: item.price,
          quantity: item.quantity,
          subtotal: item.price * item.quantity,
        })),
        total_amount: total,
        manual_discount: selectedSpecialPayment ? 0 : manualDiscount,
        voucher_code: validatedVoucher ? voucherCode : null,
        voucher_discount: selectedSpecialPayment ? 0 : voucherDiscount,
        discount_amount: selectedSpecialPayment ? 0 : totalDiscount,
        special_payment_name: selectedSpecialPayment
          ? selectedSpecialPayment.name
          : null,
        paid_amount: paymentMethod === "cash" ? Number(paymentAmount) : total,
        payment_method: selectedSpecialPayment
          ? selectedSpecialPayment.name
          : paymentMethod,
        dp_amount: paymentMethod === "partial" ? Number(dpAmount) : undefined,
        dp_payment_method: paymentMethod === "partial" ? dpMethod : undefined,
        split_payments: paymentMethod === "split" ? splitPayloads : undefined,
        change_amount:
          paymentMethod === "split"
            ? Math.max(
                0,
                splitRows.reduce((s, r) => s + Number(r.amount || 0), 0) -
                  total,
              )
            : undefined,
      };
      // Hitung jumlah print berdasarkan berat (maks mesin cuci 8kg)
      // Hanya berlaku jika ada layanan cuci — setrika saja selalu 1x print
      const hasCuci = printData.details.some(
        (d) =>
          d.item_type === "service" &&
          d.item_name.toLowerCase().includes("cuci"),
      );
      const printCopies =
        hasCuci && printData.weight_kg ? Math.ceil(printData.weight_kg / 8) : 1;
      const printAll = async () => {
        for (let i = 0; i < printCopies; i++) {
          await bluetoothPrinter
            .printReceipt(printData, posSettings)
            .catch(() => {});
        }
      };
      if (bluetoothPrinter.isConnected()) {
        printAll();
      } else if (bluetoothPrinter.isSupported()) {
        setPendingPrintData({ ...printData, printCopies });
        setShowPOSPrinterModal(true);
      }
      setCart([]);
      setSelectedCustomer(null);
      setWeightKg("");
      setTotalPcs("");
      setClothingNotes("");
      setPaymentAmount("");
      setDpAmount("");
      setDpMethod("cash");
      setSplitRows([
        { method: "cash", amount: "" },
        { method: "qris", amount: "" },
      ]);
      setDiscountAmount("");
      setDiscountType("flat");
      setVoucherCode("");
      setValidatedVoucher(null);
      setShowPaymentModal(false);
      setPaymentMethod("cash");
      setSelectedSpecialPayment(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Transaksi gagal");
    } finally {
      setLoading(false);
    }
  };

  const normalizePhoneLocal = (p) =>
    (p || "")
      .replace(/[\s\-]/g, "")
      .replace(/^\+62/, "0")
      .replace(/^62/, "0");

  const handleAddCustomer = async () => {
    if (!newCustomer.name) {
      toast.error("Nama wajib diisi");
      return;
    }
    // Client-side duplicate phone check
    if (newCustomer.phone) {
      const normalizedNew = normalizePhoneLocal(newCustomer.phone);
      const duplicate = customers.find(
        (c) => normalizePhoneLocal(c.phone || "") === normalizedNew,
      );
      if (duplicate) {
        toast.error(`Nomor HP sudah terdaftar atas nama: ${duplicate.name}`);
        return;
      }
    }
    try {
      const res = await api.post("/customers", { ...newCustomer });
      const custRes = await api.get(`/customers/${res.data.id}`);
      const created = custRes.data;
      setCustomers([...customers, created]);
      setSelectedCustomer(created);
      setShowCustomerModal(false);
      setNewCustomer({ name: "", phone: "" });
      setNewCustNameSuggestion(null);
      toast.success("Customer ditambahkan");
    } catch (error) {
      if (
        error.response?.status === 409 &&
        error.response?.data?.suggested_name
      ) {
        setNewCustNameSuggestion(error.response.data.suggested_name);
      } else {
        const msg =
          error.response?.data?.message || "Gagal menambahkan customer";
        toast.error(msg);
      }
    }
  };

  const fmt = (n) => "Rp " + Number(n).toLocaleString("id-ID");
  const fmtNum = (n) => String(n).replace(".", ","); // desimal pakai koma
  const LAUNDRY_TYPE_LABEL = {
    reguler: "Reguler",
    express: "Express",
    super_express: "Super Express",
  };

  // === REKOMENDASI CALCULATOR ===
  // Services keyed by name, loaded from services state
  // Cari layanan berdasarkan kategori + tipe — tidak bergantung pada nama layanan
  const findSvcBy = (categoryName, laundryType) =>
    services.find(
      (s) => s.category_name === categoryName && s.laundry_type === laundryType,
    );

  const hitungRekomendasi = () => {
    const berat = parseFloat(weightKg);
    if (!berat || berat <= 0) {
      toast.error("Isi berat cucian di form order terlebih dahulu");
      return;
    }
    const MAX_LOAD = 8;
    const loads = []; // detail per load untuk tampilan
    const cartMap = {}; // { svcId: { svc, qty } } untuk keranjang

    const addToCartMap = (svc, qty) => {
      if (!svc) return;
      if (cartMap[svc.id]) cartMap[svc.id].qty += qty;
      else cartMap[svc.id] = { svc, qty };
    };

    if (rekoLayanan === "cuci_kering") {
      // Cuci kering selalu reguler
      const svc = findSvcBy("Cuci Kering", "reguler");
      let rem = berat;
      while (rem > 0.01) {
        const w = Math.round(Math.min(rem, MAX_LOAD) * 10) / 10;
        const price = Number(svc?.price || 0);
        loads.push({
          weight: w,
          label: svc?.name || "Cuci Kering",
          price,
          svc,
          missing: !svc,
        });
        addToCartMap(svc, 1);
        rem = Math.round((rem - w) * 10) / 10;
      }
    } else if (rekoLayanan === "cuci_setrika") {
      if (rekoSpeed === "super_express") {
        const svc = findSvcBy("Cuci + Setrika", "super_express");
        const effectiveBerat = Math.max(berat, 2);
        const price = (svc?.price || 0) * effectiveBerat;
        const note = berat < 2 ? ` (minimum 2kg)` : "";
        loads.push({
          weight: effectiveBerat,
          label: svc?.name || "Super Express",
          price,
          svc,
          missing: !svc,
          note,
        });
        addToCartMap(svc, effectiveBerat);
      } else {
        // Cuci kering selalu reguler (20k), setrika pakai tipe rekoSpeed
        const svcPaket = findSvcBy("Cuci + Setrika", rekoSpeed);
        const svcCuci = findSvcBy("Cuci Kering", "reguler");
        const svcSetrika = findSvcBy("Setrika", rekoSpeed);
        const paketPrice = Number(svcPaket?.price || 0);
        const cuciPrice = Number(svcCuci?.price || 0);
        const setrikaPricePerKg = Number(svcSetrika?.price || 0);

        let rem = berat;
        while (rem > 0.01) {
          const w = Math.round(Math.min(rem, MAX_LOAD) * 10) / 10;
          const combiPrice = cuciPrice + w * setrikaPricePerKg;
          if (paketPrice <= combiPrice) {
            // Paket lebih murah
            loads.push({
              weight: w,
              label: svcPaket?.name || "Paket Cuci+Setrika",
              price: paketPrice,
              svc: svcPaket,
              missing: !svcPaket,
            });
            addToCartMap(svcPaket, 1);
          } else {
            // Kombinasi lebih murah
            const cuciLabel = svcCuci?.name || "Cuci Kering";
            const setrikaLabel = svcSetrika?.name || "Setrika";
            loads.push({
              weight: w,
              label: `${cuciLabel} + ${fmtNum(w)}kg × ${setrikaLabel}`,
              price: combiPrice,
              detail: `${fmt(cuciPrice)} + ${fmtNum(w)}kg × ${fmt(setrikaPricePerKg)}`,

              svcCuci,
              svcSetrika,
              setrikaQty: w,
              missing: !svcCuci || !svcSetrika,
            });
            addToCartMap(svcCuci, 1);
            addToCartMap(svcSetrika, w);
          }
          rem = Math.round((rem - w) * 10) / 10;
        }
      }
    } else if (rekoLayanan === "setrika_saja") {
      const svc = findSvcBy("Setrika", rekoSpeed);
      const price = (svc?.price || 0) * berat;
      loads.push({
        weight: berat,
        label: svc?.name || `Setrika (${rekoSpeed})`,
        price,
        svc,
        missing: !svc,
      });
      addToCartMap(svc, berat);
    }

    const total = loads.reduce((s, l) => s + l.price, 0);
    const cartItems = Object.values(cartMap);
    setRekoResult({ loads, cartItems, total, berat });
    setShowRekoModal(true);
  };

  const tambahRekoKeKeranjang = () => {
    if (!rekoResult) return;
    const missing = rekoResult.loads.filter((l) => l.missing);
    if (missing.length > 0) {
      toast.error(
        "Ada layanan yang tidak ditemukan di database. Periksa kategori & tipe laundry di menu Services.",
      );
      return;
    }
    rekoResult.cartItems.forEach(({ svc, qty }) => {
      const existing = cart.find(
        (c) => c.id === svc.id && c.type === "service",
      );
      if (existing) {
        setCart((prev) =>
          prev.map((c) =>
            c.id === svc.id && c.type === "service"
              ? { ...c, quantity: c.quantity + qty }
              : c,
          ),
        );
      } else {
        setCart((prev) => [
          ...prev,
          {
            id: svc.id,
            type: "service",
            name: svc.name,
            price: Number(svc.price),
            quantity: qty,
            maxStock: 999,
            discount: 0,
            category_name: svc.category_name || "",
            price_type: svc.price_type || null,
          },
        ]);
      }
    });
    toast.success(
      `${rekoResult.cartItems.length} layanan ditambahkan ke keranjang`,
    );
    setShowRekoModal(false);
    setActiveTab("services");
  };

  const handleRedeemLoyalty = async () => {
    if (!redeemRuleId) {
      toast.error("Pilih rule loyalti");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/transactions/redeem-free-wash", {
        customer_id: selectedCustomer.id,
        loyalty_rule_id: parseInt(redeemRuleId),
      });
      toast.success(res.data.message);
      setShowRedeemModal(false);
      setRedeemRuleId("");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Gagal redeem");
    } finally {
      setLoading(false);
    }
  };

  // Filtered items
  const servicesCats = [
    ...new Set(services.map((s) => s.category_name).filter(Boolean)),
  ];
  const productsCats = [
    ...new Set(products.map((p) => p.category_name).filter(Boolean)),
  ];
  const filteredServices = services.filter(
    (s) =>
      s.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
      (serviceFilter === "all" || s.category_name === serviceFilter),
  );
  const filteredProducts = products.filter(
    (p) =>
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
      (productFilter === "all" || p.category_name === productFilter),
  );
  const filteredCustomers = customers.filter(
    (c) =>
      c.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
      (c.phone || "").includes(customerSearch),
  );

  const paymentIcons = {
    cash: <FiDollarSign />,
    debit: <FiCreditCard />,
    credit: <FiCreditCard />,
    qris: <span style={{ fontSize: "0.8em" }}>QR</span>,
    transfer: <span style={{ fontSize: "0.8em" }}>TF</span>,
  };

  return (
    <div className="pos-container">
      {/* Shift Opening Modal - mandatory for kasir */}
      <Modal show={showShiftModal} backdrop="static" keyboard={false} centered>
        <Modal.Header>
          <Modal.Title>
            {staleShift ? "⚠️ Shift Belum Ditutup" : "Buka Shift"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {staleShift ? (
            <>
              <div className="alert alert-warning">
                <strong>
                  Shift tanggal{" "}
                  {new Date(staleShift.start_time).toLocaleDateString("id-ID", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}{" "}
                  masih terbuka!
                </strong>
                <br />
                Anda harus menutup shift kemarin sebelum bisa membuka shift hari
                ini.
              </div>
              <p className="text-muted small mb-0">
                Shift yang belum ditutup akan ditutup dengan kas akhir Rp 0.
                Anda bisa mengubahnya di halaman Manajemen Shift.
              </p>
            </>
          ) : (
            <>
              <p className="text-muted mb-3">
                Anda harus membuka shift terlebih dahulu sebelum menggunakan
                kasir. Masukkan jumlah kas awal (uang modal) di meja kasir.
              </p>
              <Form.Group>
                <Form.Label>Kas Awal (Rp)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={shiftOpeningCash}
                  onChange={(e) => setShiftOpeningCash(e.target.value)}
                  placeholder="Masukkan jumlah kas awal..."
                  autoFocus
                  onKeyDown={(e) => e.key === "Enter" && handleOpenShift()}
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {staleShift ? (
            <Button
              variant="warning"
              onClick={async () => {
                await handleCloseStaleShift();
              }}
            >
              Tutup Shift Kemarin &amp; Lanjut
            </Button>
          ) : (
            <Button variant="success" onClick={handleOpenShift}>
              Buka Shift
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {!shiftReady ? (
        <div className="text-center py-5">
          <FiClock size={48} className="text-muted mb-3" />
          <h5 className="text-muted">Menunggu shift dibuka...</h5>
        </div>
      ) : (
        <>
          {/* ===== LEFT: CATALOG ===== */}
          <div className="pos-items">
            {/* Customer Bar */}
            <div className="pos-customer-bar">
              {selectedCustomer ? (
                <div className="pos-customer-selected">
                  <div
                    className="pos-customer-info"
                    onClick={() => setShowCustomerPicker(true)}
                  >
                    <div className="pos-customer-avatar">
                      {selectedCustomer.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="pos-customer-name">
                        {selectedCustomer.name}
                      </div>
                      <div className="pos-customer-meta">
                        {selectedCustomer.phone || "No HP belum diisi"}
                      </div>
                    </div>
                  </div>
                  <button
                    className="btn btn-sm btn-link text-danger"
                    onClick={() => {
                      setSelectedCustomer(null);
                    }}
                  >
                    <FiTrash2 />
                  </button>
                </div>
              ) : (
                <button
                  className="pos-customer-placeholder"
                  onClick={() => setShowCustomerPicker(true)}
                >
                  <FiUser className="me-2" size={15} />
                  <span className="flex-grow-1 text-start">
                    Pilih Customer{" "}
                    <span style={{ color: "#e91e8c", fontWeight: 700 }}>*</span>
                  </span>
                  <FiChevronDown
                    size={14}
                    style={{ flexShrink: 0, opacity: 0.5 }}
                  />
                </button>
              )}

              {/* Laundry Order Fields - always visible */}
              <div className="row g-2 mt-2">
                <div className="col-3">
                  <Form.Label className="small fw-semibold mb-1">
                    Berat <span className="text-danger">*</span>
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="number"
                      min="0.1"
                      step="0.5"
                      value={weightKg}
                      onChange={(e) => {
                        setWeightKg(e.target.value);
                        setRekoResult(null);
                      }}
                      placeholder="0.0"
                    />
                    <InputGroup.Text>kg</InputGroup.Text>
                  </InputGroup>
                </div>
                <div className="col-3">
                  <Form.Label className="small fw-semibold mb-1">
                    Jumlah <span className="text-danger">*</span>
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="number"
                      min="1"
                      step="1"
                      value={totalPcs}
                      onChange={(e) => setTotalPcs(e.target.value)}
                      placeholder="0"
                    />
                    <InputGroup.Text>pcs</InputGroup.Text>
                  </InputGroup>
                </div>
                <div className="col-6">
                  <Form.Label className="small mb-1">
                    Catatan Pakaian
                  </Form.Label>
                  <Form.Control
                    value={clothingNotes}
                    onChange={(e) => setClothingNotes(e.target.value)}
                    placeholder="Kemeja putih, kain merah..."
                  />
                </div>
              </div>

              {/* Loyalty Progress (per customer) */}
              {selectedCustomer &&
                loyaltyEnabled &&
                (selectedCustomer.loyalty_progress || []).length > 0 && (
                  <div className="pos-loyalty-bar">
                    {(selectedCustomer.loyalty_progress || []).map((lp) => {
                      const pct = Math.min(
                        100,
                        (lp.current_count / lp.required_count) * 100,
                      );
                      const canRedeem = lp.current_count >= lp.required_count;
                      return (
                        <div
                          key={lp.rule_id}
                          className={`pos-loyalty-item ${canRedeem ? "redeemable" : ""}`}
                        >
                          <div className="pos-loyalty-info">
                            <span className="pos-loyalty-label">
                              {lp.rule_name}
                            </span>
                            <span className="pos-loyalty-count">
                              {lp.current_count}/{lp.required_count}
                            </span>
                          </div>
                          <div className="pos-loyalty-progress">
                            <div style={{ width: `${pct}%` }}></div>
                          </div>
                          {canRedeem && (
                            <button
                              className="pos-loyalty-redeem"
                              onClick={() => {
                                setRedeemRuleId(String(lp.rule_id));
                                setShowRedeemModal(true);
                              }}
                            >
                              <FiGift size={12} /> Redeem
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
            </div>

            {/* Search + Category Filter */}
            <div className="pos-search-bar">
              <div className="pos-search-input">
                <FiSearch className="pos-search-icon" />
                <input
                  type="text"
                  placeholder="Cari layanan atau produk..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    className="pos-search-clear"
                    onClick={() => setSearchTerm("")}
                  >
                    &times;
                  </button>
                )}
              </div>
            </div>

            {/* Tabs & Items */}
            <div className="pos-tabs">
              <button
                className={`pos-tab ${activeTab === "reko" ? "active" : ""}`}
                onClick={() => setActiveTab("reko")}
                style={{
                  color: activeTab === "reko" ? "#fff" : undefined,
                  background: activeTab === "reko" ? "#e91e8c" : undefined,
                }}
              >
                ✨ Rekomendasi
              </button>
              <button
                className={`pos-tab ${activeTab === "services" ? "active" : ""}`}
                onClick={() => setActiveTab("services")}
              >
                <FiClock size={14} /> Manual{" "}
                <span className="pos-tab-count">{filteredServices.length}</span>
              </button>
              <button
                className={`pos-tab ${activeTab === "products" ? "active" : ""}`}
                onClick={() => setActiveTab("products")}
              >
                <FiShoppingCart size={14} /> Produk{" "}
                <span className="pos-tab-count">{filteredProducts.length}</span>
              </button>
            </div>

            {/* Category Chips */}
            {activeTab === "services" && servicesCats.length > 1 && (
              <div className="pos-category-chips">
                <button
                  className={`pos-cat-chip ${serviceFilter === "all" ? "active" : ""}`}
                  onClick={() => setServiceFilter("all")}
                >
                  Semua
                </button>
                {servicesCats.map((cat) => (
                  <button
                    key={cat}
                    className={`pos-cat-chip ${serviceFilter === cat ? "active" : ""}`}
                    onClick={() => setServiceFilter(cat)}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}
            {activeTab === "products" && productsCats.length > 1 && (
              <div className="pos-category-chips">
                <button
                  className={`pos-cat-chip ${productFilter === "all" ? "active" : ""}`}
                  onClick={() => setProductFilter("all")}
                >
                  Semua
                </button>
                {productsCats.map((cat) => (
                  <button
                    key={cat}
                    className={`pos-cat-chip ${productFilter === cat ? "active" : ""}`}
                    onClick={() => setProductFilter(cat)}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}

            {/* Item Grid */}
            {activeTab === "reko" ? (
              <div style={{ padding: "12px" }}>
                {/* Inputs */}
                <div className="card p-3 mb-2">
                  {/* Jenis Layanan */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold small mb-2">
                      1. Jenis Layanan
                    </label>
                    <div className="d-flex gap-2">
                      {[
                        {
                          val: "cuci_kering",
                          label: "Cuci Kering",
                          desc: "Cuci + spin",
                        },
                        {
                          val: "cuci_setrika",
                          label: "Cuci + Setrika",
                          desc: "Cuci & setrika",
                        },
                        {
                          val: "setrika_saja",
                          label: "Setrika Saja",
                          desc: "Setrika only",
                        },
                      ].map(({ val, label, desc }) => (
                        <button
                          key={val}
                          type="button"
                          onClick={() => {
                            setRekoLayanan(val);
                            setRekoResult(null);
                          }}
                          className="border rounded p-2 text-center"
                          style={{
                            flex: 1,
                            minWidth: 0,
                            background:
                              rekoLayanan === val ? "#dbeafe" : "#fff",
                            borderColor:
                              rekoLayanan === val ? "#3b82f6" : "#dee2e6",
                            outline: "none",
                            cursor: "pointer",
                            transition: "all 0.15s",
                          }}
                        >
                          <div
                            style={{
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              border: `2px solid ${rekoLayanan === val ? "#3b82f6" : "#adb5bd"}`,
                              background:
                                rekoLayanan === val ? "#3b82f6" : "transparent",
                              margin: "0 auto 4px",
                            }}
                          />
                          <div
                            className="fw-semibold"
                            style={{
                              fontSize: "0.78rem",
                              color:
                                rekoLayanan === val ? "#1e40af" : "#212529",
                              lineHeight: 1.2,
                            }}
                          >
                            {label}
                          </div>
                          <div
                            className="text-muted"
                            style={{
                              fontSize: "0.68rem",
                              lineHeight: 1.2,
                              marginTop: 2,
                            }}
                          >
                            {desc}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Kecepatan */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold small mb-2">
                      2. Kecepatan
                    </label>
                    <div className="d-flex gap-2">
                      {[
                        {
                          val: "reguler",
                          label: "Reguler",
                          desc: "Est. 2-3 hari",
                        },
                        {
                          val: "express",
                          label: "Express",
                          desc: "Maks 24 jam",
                        },
                        ...(rekoLayanan === "cuci_setrika"
                          ? [
                              {
                                val: "super_express",
                                label: "Super Express",
                                desc: "Maks 5 jam",
                              },
                            ]
                          : []),
                      ].map(({ val, label, desc }) => (
                        <button
                          key={val}
                          type="button"
                          onClick={() => {
                            setRekoSpeed(val);
                            setRekoResult(null);
                          }}
                          className="border rounded p-2 text-center"
                          style={{
                            flex: 1,
                            minWidth: 0,
                            background: rekoSpeed === val ? "#cffafe" : "#fff",
                            borderColor:
                              rekoSpeed === val ? "#0e7490" : "#dee2e6",
                            outline: "none",
                            cursor: "pointer",
                            transition: "all 0.15s",
                          }}
                        >
                          <div
                            style={{
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              border: `2px solid ${rekoSpeed === val ? "#0e7490" : "#adb5bd"}`,
                              background:
                                rekoSpeed === val ? "#0e7490" : "transparent",
                              margin: "0 auto 4px",
                            }}
                          />
                          <div
                            className="fw-semibold"
                            style={{
                              fontSize: "0.78rem",
                              color: rekoSpeed === val ? "#155e75" : "#212529",
                              lineHeight: 1.2,
                            }}
                          >
                            {label}
                          </div>
                          <div
                            className="text-muted"
                            style={{
                              fontSize: "0.68rem",
                              lineHeight: 1.2,
                              marginTop: 2,
                            }}
                          >
                            {desc}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <button
                    className="btn btn-sm w-100 fw-bold"
                    style={{ background: "#e91e8c", color: "#fff" }}
                    onClick={hitungRekomendasi}
                  >
                    Hitung Rekomendasi
                  </button>
                </div>
              </div>
            ) : (
              <div className="pos-item-grid">
                {activeTab === "services" ? (
                  filteredServices.length > 0 ? (
                    filteredServices.map((svc) => (
                      <div
                        key={svc.id}
                        className="pos-item-card pos-item-service"
                        onClick={() => addToCart(svc, "service")}
                      >
                        <div className="pos-item-category">
                          {svc.category_name}
                        </div>
                        <div className="pos-item-name">{svc.name}</div>
                        <div className="pos-item-price">{fmt(svc.price)}</div>
                        <div className="pos-item-meta">
                          {svc.laundry_type
                            ? `${LAUNDRY_TYPE_LABEL[svc.laundry_type]} · `
                            : ""}
                          {svc.price_type === "per_kg" ? "per kg" : "per item"}
                        </div>
                        {cart.some(
                          (c) => c.id === svc.id && c.type === "service",
                        ) && (
                          <div className="pos-item-in-cart">
                            {cart
                              .filter(
                                (c) => c.id === svc.id && c.type === "service",
                              )
                              .reduce((s, c) => s + c.quantity, 0)}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="pos-empty">Tidak ada layanan ditemukan</div>
                  )
                ) : filteredProducts.length > 0 ? (
                  filteredProducts.map((prod) => (
                    <div
                      key={prod.id}
                      className={`pos-item-card pos-item-product ${prod.stock <= 0 ? "out-of-stock" : ""}`}
                      onClick={() =>
                        prod.stock > 0 && addToCart(prod, "product")
                      }
                    >
                      <div className="pos-item-category">
                        {prod.category_name}
                      </div>
                      <div className="pos-item-name">{prod.name}</div>
                      <div className="pos-item-price">
                        {fmt(prod.sell_price)}
                      </div>
                      <div className="pos-item-meta">
                        {prod.stock > 0 ? (
                          `Stok: ${prod.stock} ${prod.unit}`
                        ) : (
                          <span className="text-danger">Habis</span>
                        )}
                      </div>
                      {cart.some(
                        (c) => c.id === prod.id && c.type === "product",
                      ) && (
                        <div className="pos-item-in-cart">
                          {cart
                            .filter(
                              (c) => c.id === prod.id && c.type === "product",
                            )
                            .reduce((s, c) => s + c.quantity, 0)}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="pos-empty">Tidak ada produk ditemukan</div>
                )}
              </div>
            )}
          </div>

          {/* ===== RIGHT: CART ===== */}
          <div className="pos-cart">
            <div className="pos-cart-header">
              <h6 className="mb-0">
                <FiShoppingCart className="me-2" />
                Keranjang
                {totalItems > 0 && (
                  <Badge bg="primary" pill className="ms-2">
                    {totalItems}
                  </Badge>
                )}
              </h6>
            </div>

            <div className="cart-items">
              {cart.length === 0 ? (
                <div className="pos-cart-empty">
                  <FiShoppingCart size={40} />
                  <p>Keranjang kosong</p>
                  <small>Klik layanan atau produk untuk menambahkan</small>
                </div>
              ) : (
                cart.map((item, index) => (
                  <div key={index} className="pos-cart-item">
                    <div className="pos-cart-item-top">
                      <div className="pos-cart-item-info">
                        <Badge
                          bg={item.type === "service" ? "info" : "success"}
                          style={{ fontSize: "0.6rem" }}
                        >
                          {item.type === "service" ? "JASA" : "PRODUK"}
                        </Badge>
                        <span className="pos-cart-item-name">{item.name}</span>
                      </div>
                      <button
                        className="pos-cart-item-remove"
                        onClick={() => removeFromCart(index)}
                      >
                        <FiTrash2 size={13} />
                      </button>
                    </div>

                    <div className="pos-cart-item-bottom">
                      <div className="pos-cart-qty">
                        <button onClick={() => updateQuantity(index, -1)}>
                          <FiMinus size={12} />
                        </button>
                        {item.price_type === "per_kg" ? (
                          <input
                            key={`qty-${index}-${item.quantity}`}
                            type="text"
                            inputMode="decimal"
                            defaultValue={fmtNum(item.quantity)}
                            onBlur={(e) => {
                              const v = parseFloat(
                                e.target.value.replace(",", "."),
                              );
                              if (!v || v <= 0) removeFromCart(index);
                              else setQuantityDirect(index, String(v));
                            }}
                            style={{
                              width: "52px",
                              textAlign: "center",
                              border: "none",
                              background: "transparent",
                              fontWeight: 600,
                              fontSize: "0.9rem",
                            }}
                          />
                        ) : (
                          <span>{item.quantity}</span>
                        )}
                        <button onClick={() => updateQuantity(index, 1)}>
                          <FiPlus size={12} />
                        </button>
                      </div>
                      <div className="pos-cart-item-subtotal">
                        {fmt(item.price * item.quantity)}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Cart Footer */}
            <div className="cart-footer">
              {cart.length > 0 && (
                <>
                  <div className="pos-cart-summary">
                    <div className="pos-cart-summary-row">
                      <span>Subtotal ({totalItems} item)</span>
                      <span>{fmt(subtotal)}</span>
                    </div>
                    {totalDiscount > 0 && (
                      <div className="pos-cart-summary-row text-danger">
                        <span>Diskon</span>
                        <span>-{fmt(totalDiscount)}</span>
                      </div>
                    )}
                    <div className="pos-cart-summary-total">
                      <span>Total</span>
                      <span>{fmt(total)}</span>
                    </div>
                  </div>
                  <button
                    className="pos-pay-btn"
                    disabled={cart.length === 0}
                    onClick={openPayOrWarn}
                  >
                    <FiSend /> Bayar {fmt(total)}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* ===== CUSTOMER PICKER MODAL ===== */}
          <Modal
            show={showCustomerPicker}
            onHide={() => setShowCustomerPicker(false)}
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>Pilih Customer</Modal.Title>
            </Modal.Header>
            <Modal.Body
              style={{
                padding: 0,
                maxHeight: "60vh",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  padding: "12px 16px",
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                <div className="pos-search-input" style={{ marginBottom: 0 }}>
                  <FiSearch className="pos-search-icon" />
                  <input
                    type="text"
                    placeholder="Cari nama atau HP..."
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    autoFocus
                  />
                </div>
              </div>
              <div style={{ overflow: "auto", flex: 1 }}>
                {filteredCustomers.map((c) => (
                  <div
                    key={c.id}
                    className="pos-customer-option"
                    onClick={() => {
                      setSelectedCustomer(c);
                      setShowCustomerPicker(false);
                      setCustomerSearch("");
                    }}
                  >
                    <div
                      className="pos-customer-avatar"
                      style={{ width: 36, height: 36, fontSize: "0.85rem" }}
                    >
                      {c.name.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="fw-bold small">{c.name}</div>
                      <div
                        className="text-muted"
                        style={{ fontSize: "0.75rem" }}
                      >
                        {c.phone || "-"}
                      </div>
                    </div>
                  </div>
                ))}
                {filteredCustomers.length === 0 && (
                  <div className="text-center py-4 text-muted small">
                    Tidak ditemukan
                  </div>
                )}
              </div>
              <div
                style={{ padding: "12px 16px", borderTop: "1px solid #e2e8f0" }}
              >
                <Button
                  variant="outline-primary"
                  className="w-100"
                  onClick={() => {
                    setShowCustomerPicker(false);
                    setNewCustNameSuggestion(null);
                    setShowCustomerModal(true);
                  }}
                >
                  <FiPlus className="me-1" /> Tambah Customer Baru
                </Button>
              </div>
            </Modal.Body>
          </Modal>

          {/* ===== PAYMENT MODAL ===== */}
          <Modal
            show={showPaymentModal}
            onHide={() => setShowPaymentModal(false)}
            centered
            size="md"
          >
            <Modal.Body style={{ padding: "24px" }}>
              <div className="pos-payment-total">
                <small>Total Pembayaran</small>
                <div className="pos-payment-amount">{fmt(total)}</div>
                {selectedSpecialPayment && (
                  <small className="text-success d-block mt-1">
                    Override: {selectedSpecialPayment.name}
                  </small>
                )}
              </div>

              <div className="pos-payment-methods mb-3">
                {Object.entries({
                  cash: "Cash",
                  debit: "Debit/Credit",
                  qris: "QRIS",
                  transfer: "Transfer",
                }).map(([k, v]) => (
                  <button
                    key={k}
                    className={`pos-pm-btn ${paymentMethod === k && !selectedSpecialPayment ? "active" : ""}`}
                    onClick={() => {
                      setPaymentMethod(k);
                      setSelectedSpecialPayment(null);
                    }}
                  >
                    {paymentIcons[k]} {v}
                  </button>
                ))}
                <button
                  className={`pos-pm-btn unpaid ${paymentMethod === "unpaid" && !selectedSpecialPayment ? "active" : ""}`}
                  onClick={() => {
                    setPaymentMethod("unpaid");
                    setSelectedSpecialPayment(null);
                  }}
                >
                  <FiClock /> Nanti
                </button>
                <button
                  className={`pos-pm-btn unpaid ${paymentMethod === "partial" && !selectedSpecialPayment ? "active" : ""}`}
                  onClick={() => {
                    setPaymentMethod("partial");
                    setSelectedSpecialPayment(null);
                  }}
                >
                  <FiDollarSign /> DP
                </button>
                <button
                  className={`pos-pm-btn ${paymentMethod === "split" && !selectedSpecialPayment ? "active" : ""}`}
                  onClick={() => {
                    setPaymentMethod("split");
                    setSelectedSpecialPayment(null);
                  }}
                >
                  <FiCreditCard /> Split
                </button>
                {specialPaymentMethods.map((pm) => (
                  <button
                    key={`spm-${pm.id}`}
                    className={`pos-pm-btn ${selectedSpecialPayment?.id === pm.id ? "active" : ""}`}
                    onClick={() => {
                      setSelectedSpecialPayment(pm);
                      setPaymentMethod("special");
                    }}
                    title={
                      pm.override_price != null
                        ? `Override: ${fmt(pm.override_price)}`
                        : pm.name
                    }
                  >
                    <FiDollarSign /> {pm.name}
                  </button>
                ))}
              </div>

              {paymentMethod === "unpaid" && !selectedSpecialPayment && (
                <div className="alert alert-warning py-2 small mb-3">
                  Transaksi disimpan tanpa pembayaran. Bisa dibayar nanti dari
                  halaman Transaksi.
                </div>
              )}

              {paymentMethod === "partial" && !selectedSpecialPayment && (
                <div className="mb-3">
                  <div className="alert alert-info py-2 small mb-2">
                    Partial Paid: catat DP sekarang, pelunasan saat diambil.
                  </div>
                  <label className="form-label small fw-bold">
                    Jumlah DP (Down Payment)
                  </label>
                  <input
                    type="number"
                    className="form-control form-control-lg text-center"
                    value={dpAmount}
                    onChange={(e) => setDpAmount(e.target.value)}
                    placeholder="0"
                    min="1"
                    max={total - 1}
                    autoFocus
                  />
                  <div className="d-flex gap-2 mt-2">
                    {["cash", "debit", "qris", "transfer"].map((m) => (
                      <button
                        key={m}
                        type="button"
                        className={`btn btn-sm flex-fill ${
                          dpMethod === m
                            ? "btn-primary"
                            : "btn-outline-secondary"
                        }`}
                        onClick={() => setDpMethod(m)}
                      >
                        {m.toUpperCase()}
                      </button>
                    ))}
                  </div>
                  {Number(dpAmount) > 0 && Number(dpAmount) < total && (
                    <div className="mt-1 small text-muted">
                      Sisa pelunasan:{" "}
                      <strong className="text-danger">
                        {fmt(total - Number(dpAmount))}
                      </strong>
                    </div>
                  )}
                </div>
              )}

              {paymentMethod === "split" && !selectedSpecialPayment && (
                <div className="mb-3">
                  <div className="alert alert-info py-2 small mb-2">
                    Split Payment: bayar dengan beberapa metode sekaligus.
                  </div>
                  {splitRows.map((row, idx) => (
                    <div
                      key={idx}
                      className="d-flex gap-2 align-items-center mb-2"
                    >
                      <select
                        className="form-select form-select-sm"
                        style={{ width: "120px", flexShrink: 0 }}
                        value={row.method}
                        onChange={(e) => {
                          const updated = [...splitRows];
                          updated[idx] = {
                            ...updated[idx],
                            method: e.target.value,
                          };
                          setSplitRows(updated);
                        }}
                      >
                        <option value="cash">Cash</option>
                        <option value="debit">Debit</option>
                        <option value="credit">Credit</option>
                        <option value="qris">QRIS</option>
                        <option value="transfer">Transfer</option>
                      </select>
                      <input
                        type="number"
                        className="form-control form-control-sm text-end"
                        placeholder="0"
                        value={row.amount}
                        onChange={(e) => {
                          const updated = [...splitRows];
                          updated[idx] = {
                            ...updated[idx],
                            amount: e.target.value,
                          };
                          setSplitRows(updated);
                        }}
                      />
                      {splitRows.length > 2 && (
                        <button
                          className="btn btn-sm btn-outline-danger px-2"
                          onClick={() =>
                            setSplitRows(splitRows.filter((_, i) => i !== idx))
                          }
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    className="btn btn-sm btn-outline-secondary w-100 mb-2"
                    onClick={() =>
                      setSplitRows([
                        ...splitRows,
                        { method: "cash", amount: "" },
                      ])
                    }
                  >
                    + Tambah Metode
                  </button>
                  {(() => {
                    const splitTotal = splitRows.reduce(
                      (s, r) => s + Number(r.amount || 0),
                      0,
                    );
                    const diff = splitTotal - total;
                    if (splitTotal === 0) return null;
                    return (
                      <div
                        className={`small mt-1 ${Math.abs(diff) <= 1 ? "text-success" : "text-danger"}`}
                      >
                        Total dibayar: <strong>{fmt(splitTotal)}</strong>
                        {Math.abs(diff) > 1
                          ? ` — ${diff > 0 ? "lebih" : "kurang"} ${fmt(Math.abs(diff))}`
                          : " ✓ Sesuai"}
                        {diff > 1 && (
                          <span className="text-warning ms-2">
                            Kembali: {fmt(diff)}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {!selectedSpecialPayment && (
                <details className="pos-payment-extras mb-3">
                  <summary>Diskon & Voucher</summary>
                  <div className="pt-2">
                    <div className="row g-2 mb-2">
                      <div className="col-6">
                        <label className="form-label small">
                          Diskon Manual
                        </label>
                        <div className="input-group input-group-sm">
                          <select
                            className="form-select"
                            style={{ maxWidth: "70px" }}
                            value={discountType}
                            onChange={(e) => setDiscountType(e.target.value)}
                          >
                            <option value="flat">Rp</option>
                            <option value="percentage">%</option>
                          </select>
                          <input
                            type="number"
                            className="form-control"
                            value={discountAmount}
                            onChange={(e) => setDiscountAmount(e.target.value)}
                            placeholder="0"
                            min="0"
                            max={
                              discountType === "percentage" ? 100 : undefined
                            }
                          />
                        </div>
                        {discountAmount > 0 && (
                          <div className="small text-muted mt-1">
                            = {fmt(manualDiscount)}
                          </div>
                        )}
                      </div>
                      <div className="col-6">
                        <label className="form-label small">Kode Voucher</label>
                        <div className="input-group input-group-sm">
                          <input
                            type="text"
                            className="form-control"
                            value={voucherCode}
                            onChange={(e) => setVoucherCode(e.target.value)}
                            placeholder="Kode"
                          />
                          <button
                            className="btn btn-outline-primary"
                            onClick={validateVoucher}
                          >
                            Cek
                          </button>
                        </div>
                        {validatedVoucher && (
                          <div className="small text-success mt-1">
                            ✓{" "}
                            {validatedVoucher.discount_type === "percentage"
                              ? `${validatedVoucher.discount_value}%`
                              : fmt(validatedVoucher.discount_value)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </details>
              )}

              {paymentMethod === "cash" && !selectedSpecialPayment && (
                <div className="mb-3">
                  <label className="form-label small fw-bold">
                    Jumlah Bayar
                  </label>
                  <input
                    type="number"
                    className="form-control form-control-lg text-center"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    placeholder="0"
                    autoFocus
                  />
                  <div className="pos-quick-amounts">
                    {(() => {
                      const steps = [
                        1000, 5000, 10000, 20000, 50000, 100000, 200000, 500000,
                        1000000,
                      ];
                      const amounts = [
                        ...new Set(steps.map((s) => Math.ceil(total / s) * s)),
                      ]
                        .filter((v) => v >= total)
                        .slice(0, 5);
                      return amounts.map((amt) => (
                        <button
                          key={amt}
                          onClick={() => setPaymentAmount(amt.toString())}
                        >
                          {fmt(amt)}
                        </button>
                      ));
                    })()}
                  </div>
                  {Number(paymentAmount) >= total && (
                    <div className="pos-change">
                      Kembalian:{" "}
                      <strong>{fmt(Number(paymentAmount) - total)}</strong>
                    </div>
                  )}
                </div>
              )}
            </Modal.Body>
            <Modal.Footer style={{ border: "none", padding: "0 24px 24px" }}>
              <Button
                variant="light"
                onClick={() => setShowPaymentModal(false)}
                style={{ flex: 1 }}
              >
                Batal
              </Button>
              <Button
                variant={
                  (paymentMethod === "unpaid" || paymentMethod === "partial") &&
                  !selectedSpecialPayment
                    ? "warning"
                    : "primary"
                }
                onClick={handleCheckout}
                disabled={loading}
                style={{ flex: 2 }}
              >
                {loading
                  ? "Memproses..."
                  : paymentMethod === "unpaid" && !selectedSpecialPayment
                    ? "Simpan (Bayar Nanti)"
                    : paymentMethod === "partial" && !selectedSpecialPayment
                      ? `Simpan DP ${dpAmount ? fmt(Number(dpAmount)) : ""}`
                      : paymentMethod === "split" && !selectedSpecialPayment
                        ? `Bayar Split ${fmt(splitRows.reduce((s, r) => s + Number(r.amount || 0), 0))}`
                        : `Bayar ${fmt(total)}`}
              </Button>
            </Modal.Footer>
          </Modal>

          {/* ===== NEW CUSTOMER MODAL ===== */}
          <Modal
            show={showCustomerModal}
            onHide={() => {
              setShowCustomerModal(false);
              setNewCustNameSuggestion(null);
            }}
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>Customer Baru</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="row g-2 mb-3">
                <div className="col-6">
                  <Form.Label className="small">Nama *</Form.Label>
                  <Form.Control
                    value={newCustomer.name}
                    onChange={(e) => {
                      setNewCustomer({ ...newCustomer, name: e.target.value });
                      setNewCustNameSuggestion(null);
                    }}
                  />
                  {newCustNameSuggestion && (
                    <div className="mt-1">
                      <small className="text-danger">Nama sudah ada. </small>
                      <small
                        className="text-primary"
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setNewCustomer({
                            ...newCustomer,
                            name: newCustNameSuggestion,
                          });
                          setNewCustNameSuggestion(null);
                        }}
                      >
                        Pakai "{newCustNameSuggestion}"?
                      </small>
                    </div>
                  )}
                </div>
                <div className="col-6">
                  <Form.Label className="small">No. HP</Form.Label>
                  <Form.Control
                    value={newCustomer.phone}
                    onChange={(e) =>
                      setNewCustomer({ ...newCustomer, phone: e.target.value })
                    }
                  />
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="light"
                onClick={() => setShowCustomerModal(false)}
              >
                Batal
              </Button>
              <Button variant="primary" onClick={handleAddCustomer}>
                Simpan
              </Button>
            </Modal.Footer>
          </Modal>

          {/* ===== REDEEM LOYALTY MODAL ===== */}
          <Modal
            show={showRedeemModal}
            onHide={() => setShowRedeemModal(false)}
            centered
          >
            <Modal.Body className="text-center p-4">
              <div style={{ fontSize: "3rem", marginBottom: "8px" }}>🎉</div>
              <h5 className="text-success mb-1">Layanan Gratis!</h5>
              <p className="text-muted small mb-3">{selectedCustomer?.name}</p>
              <Form.Select
                className="mb-3"
                value={redeemRuleId}
                onChange={(e) => setRedeemRuleId(e.target.value)}
              >
                <option value="">-- Pilih Rule Loyalti --</option>
                {(selectedCustomer?.loyalty_progress || [])
                  .filter((lp) => lp.current_count >= lp.required_count)
                  .map((lp) => {
                    const rule = loyaltyRules.find((r) => r.id === lp.rule_id);
                    return (
                      <option key={lp.rule_id} value={lp.rule_id}>
                        {lp.rule_name} — {rule?.reward_service_name || "Gratis"}{" "}
                        ({lp.current_count}/{lp.required_count})
                      </option>
                    );
                  })}
              </Form.Select>
              <div className="d-flex gap-2">
                <Button
                  variant="light"
                  className="flex-fill"
                  onClick={() => setShowRedeemModal(false)}
                >
                  Batal
                </Button>
                <Button
                  variant="success"
                  className="flex-fill"
                  onClick={handleRedeemLoyalty}
                  disabled={loading}
                >
                  {loading ? "Memproses..." : "🎁 Redeem"}
                </Button>
              </div>
            </Modal.Body>
          </Modal>
        </>
      )}

      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirm}
        confirmLabel={confirmModal.confirmLabel}
        confirmVariant={confirmModal.confirmVariant}
      />

      {/* WA Invoice Confirmation after checkout */}
      <Modal
        show={showWAInvoiceConfirm}
        onHide={() => {
          setShowWAInvoiceConfirm(false);
          setWaInvoiceTxId(null);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FiSend className="me-2" />
            Kirim Invoice WhatsApp
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Transaksi berhasil disimpan.</p>
          <p>
            Kirim invoice WhatsApp ke <strong>{waInvoiceCustomerName}</strong> (
            {waInvoicePhone})?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowWAInvoiceConfirm(false);
              setWaInvoiceTxId(null);
            }}
          >
            Tidak
          </Button>
          <Button
            variant="success"
            disabled={waInvoiceSending}
            onClick={async () => {
              setWaInvoiceSending(true);
              try {
                const preview = await api.get(
                  `/transactions/${waInvoiceTxId}/whatsapp-preview`,
                );
                let phone = (preview.data.phone || "").replace(/[^0-9]/g, "");
                if (phone.startsWith("0")) phone = "62" + phone.slice(1);
                else if (phone.startsWith("8")) phone = "62" + phone;
                const waUrl = `https://wa.me/${phone}?text=${encodeURIComponent(preview.data.message || "")}`;
                window.open(waUrl, "_blank");
                toast.success("WhatsApp Web dibuka!");
              } catch {
                toast.error("Gagal membuka WhatsApp Web");
              } finally {
                setWaInvoiceSending(false);
                setShowWAInvoiceConfirm(false);
                setWaInvoiceTxId(null);
              }
            }}
          >
            {waInvoiceSending ? "Membuka..." : "Kirim WhatsApp"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reko Result Modal */}
      <Modal
        show={showRekoModal}
        onHide={() => setShowRekoModal(false)}
        centered
      >
        <Modal.Header closeButton style={{ borderBottom: "2px solid #e91e8c" }}>
          <Modal.Title style={{ fontSize: "1rem", color: "#c2185b" }}>
            ✨ Rekomendasi untuk {fmtNum(rekoResult?.berat)} kg
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <table className="table table-sm mb-0">
            <thead className="table-light">
              <tr>
                <th className="ps-3">Load</th>
                <th>Layanan</th>
                <th className="text-end pe-3">Harga</th>
              </tr>
            </thead>
            <tbody>
              {(rekoResult?.loads || []).map((load, i) => (
                <tr key={i}>
                  <td
                    className="text-muted ps-3"
                    style={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}
                  >
                    {fmtNum(load.weight)}kg
                  </td>
                  <td>
                    <div
                      className="fw-semibold"
                      style={{ fontSize: "0.85rem" }}
                    >
                      {load.label}
                    </div>
                    {load.detail && (
                      <small className="text-muted d-block">
                        {load.detail}
                      </small>
                    )}
                    {load.note && (
                      <small className="text-muted d-block">{load.note}</small>
                    )}
                    {load.missing && (
                      <span
                        className="badge bg-danger"
                        style={{ fontSize: "0.65rem" }}
                      >
                        Layanan tidak ada di DB
                      </span>
                    )}
                  </td>
                  <td className="text-end fw-bold pe-3">{fmt(load.price)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: "2px solid #e91e8c" }}>
                <td
                  colSpan={2}
                  className="fw-bold ps-3"
                  style={{ color: "#c2185b" }}
                >
                  TOTAL
                </td>
                <td
                  className="text-end fw-bold pe-3"
                  style={{ color: "#c2185b", fontSize: "1.05rem" }}
                >
                  {fmt(rekoResult?.total)}
                </td>
              </tr>
            </tfoot>
          </table>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowRekoModal(false)}
          >
            Tutup
          </Button>
          <Button
            size="sm"
            style={{ background: "#e91e8c", border: "none" }}
            onClick={tambahRekoKeKeranjang}
          >
            <FiShoppingCart className="me-1" /> Tambah ke Keranjang
          </Button>
        </Modal.Footer>
      </Modal>

      {/* POS Printer Connect Modal */}
      <Modal
        show={showPOSPrinterModal}
        onHide={() => setShowPOSPrinterModal(false)}
        centered
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title>Printer Belum Terhubung</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Hubungkan printer Bluetooth untuk mencetak struk transaksi ini?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowPOSPrinterModal(false);
              setPendingPrintData(null);
            }}
            disabled={posPrinterConnecting}
          >
            Lewati
          </Button>
          <Button
            variant="primary"
            disabled={posPrinterConnecting}
            onClick={async () => {
              setPosPrinterConnecting(true);
              const res = await bluetoothPrinter.connect();
              setPosPrinterConnecting(false);
              setShowPOSPrinterModal(false);
              if (!res.success) {
                toast.error(`Gagal terhubung ke printer: ${res.error}`);
                setPendingPrintData(null);
                return;
              }
              if (pendingPrintData) {
                const copies = pendingPrintData.printCopies || 1;
                const printPending = async () => {
                  for (let i = 0; i < copies; i++) {
                    await bluetoothPrinter
                      .printReceipt(pendingPrintData, posSettings)
                      .catch(() => {});
                  }
                };
                printPending();
                setPendingPrintData(null);
              }
            }}
          >
            {posPrinterConnecting ? "Menghubungkan..." : "Hubungkan & Cetak"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default POS;
